import {
  BulkActionCount,
  DisplayFieldErrors,
  SharedserviceMelcoModelsAlphabetListItem,
  useTableSelection,
} from "melco-shared-logic";
import isEmpty from "lodash-es/isEmpty";
import {
  Button,
  DesignTokens,
  FullWidthSpace,
  PlusIcon,
  Space,
  Table,
  Title,
  ValueOrFallback,
} from "melco-ui";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import get from "lodash-es/get";
import { useAddFonts } from "../../../hooks/libraries/fontCollections/useAddFonts";
import { BulkActionRemoveFonts } from "./BulkActionRemoveFonts";

const { colors, spacings } = DesignTokens;

const EmptyFontCollectionWrapper = styled.div`
  border: 1px dashed ${colors.gray.middle};
  padding: ${spacings.middle}px;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TableWrapper = styled.div`
  margin-left: -${spacings.xlarge}px;
  margin-right: -${spacings.xlarge}px;
`;

const AddButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

type FontCollectionFontsListProps = {
  setDrawerOpen: (open: boolean) => void;
};

export const FontCollectionFontsList: React.FC<
  FontCollectionFontsListProps
> = ({ setDrawerOpen }) => {
  const { t } = useTranslation();

  const { selectedRowKeys, setSelectedRowKeys, rowSelection } =
    useTableSelection<SharedserviceMelcoModelsAlphabetListItem>(() => { });

  const { fonts } = useAddFonts();

  const title = (
    <Title level={5}>{t("font_collections.actions.add_fonts.title")}</Title>
  );

  const addButton = (
    <Button
      type="primary"
      icon={<PlusIcon />}
      onClick={() => setDrawerOpen(true)}
    >
      {t("font_collections.actions.add_fonts.button")}
    </Button>
  );

  if (isEmpty(fonts)) {
    return (
      <div>
        {title}

        <DisplayFieldErrors name="alphabet_list" />

        <EmptyFontCollectionWrapper>{addButton}</EmptyFontCollectionWrapper>
      </div>
    );
  }

  const sorter = (dataIndex: string) => (a: any, b: any) =>
    String(get(a, dataIndex)).localeCompare(String(get(b, dataIndex)));

  const columns = [
    {
      title: t("fonts_list.columns.name"),
      key: "name",
      dataIndex: "name",
      sorter: sorter("name"),
      render: (_: string, item: SharedserviceMelcoModelsAlphabetListItem) => (
        <ValueOrFallback
          value={item.name}
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t("fonts_list.columns.type"),
      key: "type",
      dataIndex: "type",
      sorter: true,
    },
  ];
  return (
    <div>
      <TableHeader>
        {title}

        <Space size="middle">
          <BulkActionCount selectedCount={selectedRowKeys.length} />
          <BulkActionRemoveFonts
            allFonts={fonts}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
          />
        </Space>
      </TableHeader>

      <DisplayFieldErrors name="alphabet_list" />

      <FullWidthSpace direction="vertical" size="large">
        <TableWrapper>
          <Table
            showSorterTooltip={false}
            dataSource={fonts}
            columns={columns}
            rowKey="id"
            pagination={false}
            rowSelection={rowSelection}
            data-cy="fonts-table"
          />
        </TableWrapper>
        <AddButtonWrapper>{addButton}</AddButtonWrapper>
      </FullWidthSpace>
    </div>
  );
};
